// frontend/features/mpc-common/src/features/Bidding/SupplierQuoteDrawer/Drawer.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import Decimal from 'decimal.js';
import {
  Drawer,
  Button,
  Space,
  Tabs,
  Message,
  Modal,
  Typography,
  Form,
} from '@bedrock/components';
import { CheckCircle, Save, Download, Upload } from '@bedrock/icons-react';
import { useSForm, SFormItem } from '@mpc/common/src/components/SForm';
import { useImportFileModal } from '@mpc/common/src/features/importFileModal';
import BiddingSupplierQuoteTable from './Table';
import { fillComputedColumns } from './handleColumns';
import { buildExportQuoteDetails, buildImportConfigs } from './apiAdapter';
import {
  outsourceBiddingCheckQuoteDetail,
  outsourceCommonSaveTemporary,
  outsourceCommonDeleteTemporary,
  outsourceBiddingExportQuoteDetail,
  outsourceBiddingImportQuoteDetail,
  biddingExportQuoteDetail,
} from '@mpc/common/src/services/api';
import { $sType, getVendorStorage } from '@mpc/common/src/utils';
import type {
  BiddingSupplierQuoteTab,
  GetQuoteDetailResponse,
  ViewMode,
} from './types';
import withFunctionCall from '../../../utils/withFunctionCall';
import './index.less';

interface Props {
  supplierId: number;
  demandId: number;
  viewMode: ViewMode;
  initialData: GetQuoteDetailResponse;
  /** 校验通过后回调，传递 tabs 数据回父组件 */
  onCommit?: (tabs: Record<string, any>[]) => void;
  onClose?: () => void;
  /** 命令式 close (由 withFunctionCall 注入) */
  close?: () => void;
}

const BiddingSupplierQuoteDrawer: React.FC<Props> = ({
  supplierId,
  demandId,
  viewMode,
  initialData,
  onCommit,
  onClose,
  close,
}) => {
  const [loading, setLoading] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(
    initialData.last_temp_save_time
  );
  const [activeKey, setActiveKey] = useState<string>('0');

  const initialDataRef = useRef<string>('');
  const { form } = useSForm();

  const isEdit = viewMode === 'edit';
  const drawerTitle = isEdit ? '报价总明细' : '报价信息';

  // Tab 列表结构不变（编辑期间不增删 tab），直接用 initialData
  const tabs: BiddingSupplierQuoteTab[] = initialData.tabs;

  // ----------------- 初始化表单数据 -----------------
  useEffect(() => {
    if (tabs && !initialDataRef.current) {
      form.setFieldsValue({ tabs });
      initialDataRef.current = JSON.stringify(tabs);
    }
  }, [form, tabs]);

  // ----------------- Tab 切换 -----------------
  const handleTabChange = useMemoizedFn((key: string) => {
    setActiveKey(key);
  });

  // ----------------- 暂存 -----------------
  const handleTempSave = useMemoizedFn(async () => {
    try {
      setLoading(true);
      const formValues = form.getFieldsValue();
      let tabs = formValues.tabs ?? [];
      tabs = fillComputedColumns(tabs);
      const { vendorId, tenantCode } = getVendorStorage();
      const code = `${vendorId}_${tenantCode}_${demandId}`;
      await outsourceCommonSaveTemporary({
        type: 'outsource_bidding_quote',
        code,
        content: JSON.stringify(tabs),
      });
      onCommit?.(tabs);
      Message.info('暂存成功');
      close?.();
    } catch (e: any) {
      Message.error(e?.message || '暂存失败');
    } finally {
      setLoading(false);
    }
  });

  // ----------------- 提交（校验） -----------------
  const handleCommit = useMemoizedFn(async () => {
    setLoading(true);
    try {
      await form.validateFields();
      const formValues = form.getFieldsValue();
      let tabs: any[] = formValues.tabs ?? [];
      tabs = fillComputedColumns(tabs);

      // 计算所有 tab 行中 priceAmount 的合计
      const quoteTotalAmount = tabs.reduce((sum: Decimal, tab: any) => {
        const rows = tab.rows ?? [];
        return rows.reduce((tabSum: Decimal, row: any) => {
          const val = row?.priceAmount;
          if (val === undefined || val === null || val === '') return tabSum;
          return tabSum.plus(new Decimal(val));
        }, sum);
      }, new Decimal(0));

      // 调用外包端校验接口
      await outsourceBiddingCheckQuoteDetail({
        tabs: tabs.map((tab) => ({
          config_id: tab.config_id,
          rows: tab.rows,
        })),
        quote_total_amount: quoteTotalAmount.toNumber(),
      });

      Message.info('校验通过');

      // 删除服务端暂存数据（不阻塞主流程）
      try {
        const { vendorId, tenantCode } = getVendorStorage();
        const code = `${vendorId}_${tenantCode}_${demandId}`;
        await outsourceCommonDeleteTemporary({
          type: 'outsource_bidding_quote',
          code,
        });
      } catch (e) {
        console.warn('删除暂存数据失败', e);
      }

      // 先关闭抽屉，再回调父组件（避免回调异常阻塞关闭）
      close?.();
      onCommit?.(tabs);
    } catch (e: any) {
      if (e?.errorFields?.length) {
        Message.error('报价信息未填写完整');
      } else {
        Message.error(e?.message || '数据校验失败');
      }
    } finally {
      setLoading(false);
    }
  });

  // ----------------- 导出 / 下载 -----------------
  const handleExport = useMemoizedFn(async () => {
    try {
      const currentTabs: BiddingSupplierQuoteTab[] =
        form.getFieldValue('tabs') ?? [];
      if (!currentTabs.length) {
        Message.info('暂无数据可导出');
        return;
      }
      const service =
        $sType === 'inner'
          ? biddingExportQuoteDetail
          : outsourceBiddingExportQuoteDetail;
      await service(
        {
          quote_details: buildExportQuoteDetails(currentTabs),
        },
        { dataType: 'blob' }
      );
    } catch (e: any) {
      Message.error(e?.message || '导出失败');
    }
  });

  // ----------------- 导入 -----------------
  const importType = useMemo(
    () => `bidding_supplier_quote_detail_${demandId}`,
    [demandId]
  );

  const { openImportFileModal, closeImportFileModal } = useImportFileModal({
    importTitle: '导入报价明细',
    importType,
  });

  const handleImport = useMemoizedFn(() => {
    const currentTabs: BiddingSupplierQuoteTab[] =
      form.getFieldValue('tabs') ?? [];
    if (!currentTabs.length) {
      Message.info('暂无数据可导入');
      return;
    }
    const configs = buildImportConfigs(currentTabs);

    const extraFormData = { configs: JSON.stringify(configs) };
    openImportFileModal({
      importTitle: '导入报价明细',
      importType,
      extraFormData,
      uploadFinishedCallback: (data) => {
        if (data.code === 1 && data?.data?.fail_count === 0) {
          const importedRows = data?.data?.data ?? [];
          if (!importedRows.length) {
            closeImportFileModal();
            return;
          }

          const currentTabs: BiddingSupplierQuoteTab[] =
            form.getFieldValue('tabs') ?? [];

          // 计算可编辑列规则（与 BiddingSupplierQuoteTable 保持一致）
          const canEditColumnCategories = isEdit ? [2, 3] : [];
          const respectOutsourceModify = $sType === 'outer' && isEdit;

          // 按 config_id 分组导入行
          const importedByConfigId = new Map<number, any[]>();
          importedRows.forEach((row: any) => {
            const configId = row.__config_id__;
            if (configId != null) {
              if (!importedByConfigId.has(configId)) {
                importedByConfigId.set(configId, []);
              }
              importedByConfigId.get(configId)!.push(row);
            }
          });

          // 更新每个 tab 的 rows
          const updatedTabs = currentTabs.map((tab) => {
            const imported = importedByConfigId.get(tab.config_id);
            if (!imported) return tab;

            // 构建 column code -> type 映射 + 不可编辑列集合
            const columnTypeMap = new Map<string, string>();
            const uneditableColumnCodes = new Set<string>();
            (tab.columns ?? []).forEach((col: any) => {
              columnTypeMap.set(col.code, col.type);
              // 判断列是否可编辑（与 handleColumns.isCellReadOnly 白名单模式一致）
              const isEditable =
                canEditColumnCategories.includes(col.category) ||
                (respectOutsourceModify && !!col.outsource_modify);
              if (!isEditable) {
                uneditableColumnCodes.add(col.code);
              }
            });

            const updatedRows = [...(tab.rows ?? [])];

            imported.forEach((importRow: any) => {
              const rowIndex = importRow.__row_index__;
              if (
                rowIndex == null ||
                rowIndex < 0 ||
                rowIndex >= updatedRows.length
              )
                return;

              // 以原行为基础，覆盖导入数据（跳过不可编辑列，保留原值）
              const targetRow = { ...updatedRows[rowIndex] };

              Object.keys(importRow).forEach((key) => {
                // 跳过元数据字段
                if (key.startsWith('__')) return;
                // 跳过不可编辑列（保留原行数据）
                if (uneditableColumnCodes.has(key)) return;

                const rawValue = importRow[key];
                const colType = columnTypeMap.get(key);

                if (colType) {
                  if (colType === 'text_with_upload') {
                    targetRow[key] = { text: String(rawValue), files: [] };
                  } else {
                    targetRow[key] = rawValue;
                  }
                } else {
                  // 非列字段（segment_code, priceAmount, taxRate 等行级属性）直接赋值
                  targetRow[key] = rawValue;
                }
              });

              updatedRows[rowIndex] = targetRow;
            });

            return { ...tab, rows: updatedRows };
          });

          form.setFieldsValue({ tabs: updatedTabs });
          closeImportFileModal();
          Message.success(`导入成功，共更新 ${importedRows.length} 条报价明细`);
        }
      },
    });
  });

  // ----------------- 关闭 -----------------
  const handleClose = useMemoizedFn(() => {
    if (isEdit) {
      const currentTabs = form.getFieldValue('tabs');
      const hasChanges = JSON.stringify(currentTabs) !== initialDataRef.current;
      if (hasChanges) {
        Modal.confirm({
          title: '有未保存修改，确定关闭吗？',
          content: '关闭后未保存的修改将丢失',
          onOk: () => {
            onClose?.();
            close?.();
          },
        });
        return;
      }
    }
    onClose?.();
    close?.();
  });

  // ----------------- 顶部按钮 -----------------
  const titleExtra = useMemo(() => {
    if (isEdit) {
      return [
        <Button
          key="export"
          size={'small'}
          type="checked-neutral"
          icon={<Download />}
          onClick={handleExport}
        >
          导出
        </Button>,
        <Button
          size={'small'}
          key="import"
          type="checked"
          icon={<Upload />}
          onClick={handleImport}
        >
          导入
        </Button>,
      ];
    }
    return [
      <Button
        size={'small'}
        key="download"
        type="checked-neutral"
        icon={<Download />}
        onClick={handleExport}
      >
        下载
      </Button>,
    ];
  }, [isEdit, handleExport, handleImport]);

  // ----------------- 底部按钮 -----------------
  const footer = useMemo(() => {
    if (!isEdit) return null;
    return (
      <div className="flex justify-end">
        <Space size={12}>
          {lastSavedAt && (
            <Typography.Text type="secondary">
              <CheckCircle /> {lastSavedAt} 已保存
            </Typography.Text>
          )}
          <Button loading={loading} icon={<Save />} onClick={handleTempSave}>
            暂存
          </Button>
          <Button type="primary" loading={loading} onClick={handleCommit}>
            提交
          </Button>
        </Space>
      </div>
    );
  }, [isEdit, lastSavedAt, loading, handleTempSave, handleCommit]);

  return (
    <Form form={form}>
      <Drawer
        placement="bottom"
        visible
        maskClosable={false}
        bodyStyle={{ paddingTop: 0 }}
        title={drawerTitle}
        titleExtra={titleExtra}
        onClose={handleClose}
        footer={footer}
        height="80vh"
      >
        <Form.List name="tabs">
          {(fields) => {
            const currentTabs: BiddingSupplierQuoteTab[] =
              form.getFieldValue('tabs') ?? [];
            return (
              <Tabs
                activeKey={activeKey}
                onChange={handleTabChange}
                destroyInactiveTabPane={false}
              >
                {fields.map((field) => {
                  const tab = currentTabs[field.name];
                  if (!tab) return null;
                  const tabLabel =
                    tab.demand_category_names?.join(',') ??
                    (tab as any).demand_category_name ??
                    '';
                  return (
                    <Tabs.TabPane
                      key={String(field.name)}
                      tab={tabLabel}
                      forceRender
                    >
                      <SFormItem
                        formItemKey={[field.name, 'rows']}
                        watchChildFormValidate
                        validateTrigger={false}
                        className={'bidding-quote-table-form-item'}
                        noLabel
                        // rules={isEdit ? [{ validator: runValidator }] : undefined}
                        render={() => (
                          <BiddingSupplierQuoteTable
                            readOnly={!isEdit}
                            schemaColumns={tab.columns ?? []}
                            viewMode={viewMode}
                            canEditColumnCategories={isEdit ? [2, 3] : []}
                            respectOutsourceModify={
                              $sType === 'outer' && isEdit
                            }
                          />
                        )}
                      />
                    </Tabs.TabPane>
                  );
                })}
              </Tabs>
            );
          }}
        </Form.List>
      </Drawer>
    </Form>
  );
};

export default withFunctionCall(BiddingSupplierQuoteDrawer);
